"use client";

import { useState, useEffect } from "react";
import { Trophy, Timer, TrendingUp, Flame } from "lucide-react";
import { useVisibilityRef } from "@/hooks/useVisibilityRef";

interface SportsContextSidebarProps {
  sportLeague: string; // e.g., "uefa.champions", "eng.1", "nba"
  leagueName?: string;
}

interface TeamStanding {
  rank: number;
  team: string;
  abbreviation: string;
  played: number;
  wins: number;
  draws: number;
  losses: number;
  points: number;
  goalsFor?: number;
  goalsAgainst?: number;
  goalDiff?: number;
}

interface Match {
  id: string;
  homeTeam: string;
  homeAbbr: string;
  homeScore: number;
  awayTeam: string;
  awayAbbr: string;
  awayScore: number;
  status: "scheduled" | "in_progress" | "final";
  statusDetail: string;
  startTime: string;
}

// League display config
const LEAGUE_CONFIG: Record<string, { name: string; color: string; isSoccer: boolean }> = {
  "uefa.champions": { name: "Champions League", color: "#1e3a8a", isSoccer: true },
  "uefa.europa": { name: "Europa League", color: "#f97316", isSoccer: true },
  "eng.1": { name: "Premier League", color: "#3d195b", isSoccer: true },
  "esp.1": { name: "La Liga", color: "#ee324e", isSoccer: true },
  "ita.1": { name: "Serie A", color: "#008fd7", isSoccer: true },
  "ger.1": { name: "Bundesliga", color: "#d20515", isSoccer: true },
  "fra.1": { name: "Ligue 1", color: "#dae025", isSoccer: true },
  "fifa.world": { name: "FIFA World Cup", color: "#56042c", isSoccer: true },
  "nba": { name: "NBA", color: "#c9082a", isSoccer: false },
  "nfl": { name: "NFL", color: "#013369", isSoccer: false },
  "mlb": { name: "MLB", color: "#002d72", isSoccer: false },
  "nhl": { name: "NHL", color: "#000000", isSoccer: false },
};

export default function SportsContextSidebar({ sportLeague, leagueName }: SportsContextSidebarProps) {
  const [standings, setStandings] = useState<TeamStanding[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [liveCount, setLiveCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [league, setLeague] = useState(leagueName || "");
  const visRef = useVisibilityRef();

  const config = LEAGUE_CONFIG[sportLeague] || { name: leagueName || "Sports", color: "#CCFF00", isSoccer: false };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const res = await fetch(`/api/sports/${sportLeague}`);
        if (res.ok) {
          const data = await res.json();
          setStandings(data.standings || []);
          setMatches(data.matches || []);
          setLiveCount(data.liveCount || 0);
          setLeague(data.league || leagueName || "");
        }
      } catch (error) {
      }

      setLoading(false);
    };

    fetchData();

    // Refresh every 60 seconds
    const interval = setInterval(() => { if (visRef.current) fetchData(); }, 60000);
    return () => clearInterval(interval);
  }, [sportLeague, leagueName]);

  if (loading) {
    return (
      <div className="bg-[#18181D] rounded-[5px] border border-gray-800 p-4">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="w-5 h-5 text-gray-400 animate-pulse" />
          <span className="text-white font-satoshi font-bold">Loading {config.name}...</span>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-10 bg-[#18181D]/50 rounded-[5px]" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const liveMatches = matches.filter(m => m.status === "in_progress");
  const upcomingMatches = matches.filter(m => m.status === "scheduled").slice(0, 3);

  return (
    <div className="space-y-4">
      {/* Live Matches */}
      {liveMatches.length > 0 && (
        <div className="bg-[#18181D] border border-[#FF1744] rounded-[5px] overflow-hidden">
          <div className="bg-gradient-to-r from-[#FF1744] to-[#FF5252] px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-white rounded-[5px] animate-pulse" />
              <span className="text-white text-xs font-inter-tight font-bold uppercase tracking-wider">
                LIVE NOW
              </span>
            </div>
            <span className="text-white/80 text-xs font-inter-tight">
              {liveCount} {liveCount === 1 ? "match" : "matches"}
            </span>
          </div>

          <div className="p-3 space-y-3">
            {liveMatches.slice(0, 2).map((match) => (
              <div key={match.id} className="bg-[#18181D]/50 rounded-[5px] p-3">
                <div className="flex items-center justify-between text-[10px] text-gray-400 mb-2">
                  <span className="flex items-center gap-1">
                    <Timer className="w-3 h-3" />
                    {match.statusDetail}
                  </span>
                </div>

                {/* Home Team */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-[5px] bg-white/10 flex items-center justify-center text-[10px] font-bold text-white">
                      {match.homeAbbr}
                    </div>
                    <span className="text-white font-satoshi font-bold text-sm">
                      {match.homeTeam.split(' ').pop()}
                    </span>
                  </div>
                  <span className="text-xl font-monument font-bold text-white">
                    {match.homeScore}
                  </span>
                </div>

                {/* Away Team */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-[5px] bg-gray-500/20 flex items-center justify-center text-[10px] font-bold text-gray-400">
                      {match.awayAbbr}
                    </div>
                    <span className="text-white font-satoshi font-bold text-sm">
                      {match.awayTeam.split(' ').pop()}
                    </span>
                  </div>
                  <span className="text-xl font-monument font-bold text-white">
                    {match.awayScore}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming Matches */}
      {upcomingMatches.length > 0 && liveMatches.length === 0 && (
        <div className="bg-[#18181D] border border-gray-800 rounded-[5px] overflow-hidden">
          <div className="border-b border-gray-800 px-4 py-3">
            <div className="flex items-center gap-2">
              <Timer className="w-4 h-4 text-gray-400" />
              <h3 className="text-white font-inter-tight font-bold text-sm uppercase tracking-wider">
                Upcoming
              </h3>
            </div>
          </div>
          <div className="p-3 space-y-2">
            {upcomingMatches.map((match) => (
              <div
                key={match.id}
                className="flex items-center justify-between bg-[#0f0f12]/50 rounded-[5px] p-2.5"
              >
                <div className="flex items-center gap-2 flex-1">
                  <span className="text-white font-satoshi font-bold text-xs">
                    {match.homeAbbr}
                  </span>
                  <span className="text-gray-500 text-xs">vs</span>
                  <span className="text-white font-satoshi font-bold text-xs">
                    {match.awayAbbr}
                  </span>
                </div>
                <span className="text-gray-400 text-xs font-inter-tight">
                  {new Date(match.startTime).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Standings */}
      <div className="bg-[#18181D] border border-gray-800 rounded-[5px] overflow-hidden hover:border-gray-500 transition-colors">
        <div className="border-b border-gray-800 px-4 py-3">
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-gray-400" />
            <h3 className="text-white font-inter-tight font-bold text-sm uppercase tracking-wider">
              {league || config.name} Standings
            </h3>
          </div>
        </div>

        {/* Table Header */}
        <div className="px-3 py-2 border-b border-[#1A1A1A]/30 flex items-center gap-2 text-[10px] text-gray-500 font-inter-tight uppercase">
          <span className="w-6 text-center">#</span>
          <span className="flex-1">Team</span>
          <span className="w-8 text-center">P</span>
          <span className="w-8 text-center">W</span>
          {config.isSoccer && <span className="w-8 text-center">D</span>}
          <span className="w-8 text-center">L</span>
          {config.isSoccer && <span className="w-10 text-center">PTS</span>}
        </div>

        <div className="p-2 space-y-1 max-h-[350px] overflow-y-auto scrollbar-hide">
          {standings.slice(0, 15).map((team, idx) => {
            const isTop4 = team.rank <= 4;
            const isTop8 = team.rank <= 8;

            return (
              <div
                key={idx}
                className={`flex items-center gap-2 p-2 rounded-[5px] transition-colors hover:bg-[#0f0f12] ${
                  isTop4
                    ? "bg-[#CCFF00]/10 border border-[#CCFF00]/20"
                    : isTop8
                    ? "bg-[#00FF9D]/5 border border-[#00FF9D]/10"
                    : "bg-[#0f0f12]/30"
                }`}
              >
                <span
                  className={`w-6 text-center font-inter-tight font-bold text-xs ${
                    team.rank === 1
                      ? "text-[#FFD700]"
                      : team.rank <= 4
                      ? "text-[#CCFF00]"
                      : team.rank <= 8
                      ? "text-[#00FF9D]"
                      : "text-gray-500"
                  }`}
                >
                  {team.rank}
                </span>
                <div className="w-6 h-6 rounded-[5px] bg-[#1A1A1A] flex items-center justify-center">
                  <span className="text-[8px] font-bold text-gray-400">
                    {team.abbreviation?.slice(0, 3) || team.team.slice(0, 3).toUpperCase()}
                  </span>
                </div>
                <span className="flex-1 text-white font-satoshi text-xs truncate">
                  {team.team}
                </span>
                <span className="w-8 text-center text-gray-400 text-xs font-inter-tight">
                  {team.played}
                </span>
                <span className="w-8 text-center text-green-400 text-xs font-inter-tight font-bold">
                  {team.wins}
                </span>
                {config.isSoccer && (
                  <span className="w-8 text-center text-gray-400 text-xs font-inter-tight">
                    {team.draws}
                  </span>
                )}
                <span className="w-8 text-center text-red-400 text-xs font-inter-tight">
                  {team.losses}
                </span>
                {config.isSoccer && (
                  <span className="w-10 text-center text-white text-xs font-inter-tight font-bold">
                    {team.points}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        {config.isSoccer && (
          <div className="px-3 pb-3 pt-1 border-t border-[#1A1A1A]/30">
            <div className="flex items-center gap-4 text-[10px] text-gray-500">
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-[5px] bg-[#CCFF00]" />
                Top 4
              </span>
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-[5px] bg-[#00FF9D]" />
                Top 8
              </span>
            </div>
          </div>
        )}
      </div>

      {/* League Info */}
      <div className="bg-[#18181D] border border-gray-800 rounded-[5px] p-4">
        <div className="flex items-center gap-2 mb-3">
          <Flame className="w-4 h-4 text-gray-400" />
          <h3 className="text-white font-inter-tight font-bold text-sm">{config.name}</h3>
        </div>
        <div className="space-y-2 text-xs">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-3 h-3 text-gray-400" />
            <span className="text-gray-300 font-satoshi">Live data from ESPN</span>
          </div>
        </div>
      </div>
    </div>
  );
}
